export function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export type CmsRichImageVariant = 'article' | 'changelog' | 'painel';

/** Wrapper redimensionável (arrastar canto) + img — mesmo padrão visual por variant. */
export function buildResizableImageHtml(
  src: string,
  alt: string,
  variant: CmsRichImageVariant,
): string {
  const border =
    variant === 'changelog'
      ? 'border border-white/10'
      : variant === 'painel'
        ? 'border border-white/15'
        : 'border border-gray-200';
  const srcE = escapeAttr(src);
  const altE = escapeAttr(alt);
  return (
    `<span class="cms-rt-resize inline-block max-w-full align-middle select-none" contenteditable="false" ` +
    `style="resize:both;overflow:auto;width:min(100%,420px);min-width:120px;min-height:72px;max-width:100%;line-height:0">` +
    `<img src="${srcE}" alt="${altE}" class="block h-auto max-w-full object-contain rounded-lg ${border}" ` +
    `style="width:100%;height:auto" draggable="false" loading="lazy" decoding="async" />` +
    `</span>`
  );
}

export function buildChangelogFigureHtml(
  src: string,
  alt: string,
  caption: string | undefined,
  variant: CmsRichImageVariant = 'changelog',
): string {
  const inner = buildResizableImageHtml(src, alt, variant);
  const cap = caption?.trim();
  if (!cap) return inner;
  const capE = escapeAttr(cap);
  return (
    `<figure class="changelog-asset my-5">` +
    `${inner}` +
    `<figcaption class="mt-2 text-center font-inter text-[11px] text-slate-400">${capE}</figcaption>` +
    `</figure>`
  );
}

/** Smilie inline (gs.xatblog.net) após validação na API. */
export function buildInlineSmilieImgHtml(name: string, url: string): string {
  const srcE = escapeAttr(url);
  const label = escapeAttr(`(${name})`);
  const nameE = escapeAttr(name);
  return (
    `<img src="${srcE}" alt="${label}" title="${label}" width="32" height="32" ` +
    `class="inline-block h-8 w-8 max-h-8 max-w-[32px] object-contain align-text-bottom" ` +
    `data-xat-smilie="${nameE}" draggable="false" loading="lazy" decoding="async" />`
  );
}

export const SMILIE_INNER_RE = /^[a-zA-Z0-9_-]{1,48}$/;

/** Normaliza entrada do utilizador para nome interno do smilie. */
export function normalizeSmilieNameInput(raw: string): string {
  let s = raw.trim();
  const outerParens = /^\(([^)]+)\)$/.exec(s);
  if (outerParens) s = outerParens[1].trim();
  return s.replace(/[()]/g, '').trim();
}

export function isLikelyHtmlContent(value: string): boolean {
  return /<[a-z][\s\S]*>/i.test(value.trim());
}

export function stripHtmlToText(html: string): string {
  const raw = html.trim();
  if (!raw) return '';
  const withoutTags = isLikelyHtmlContent(raw)
    ? raw
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/p>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
    : raw;
  return withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/&#x0*a0;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
}
